import { telecharger as telechargerFichier } from '../../lib/telecharger'
// L'API des études (CDC du 14/10/2026, § 7.3, préfixe `/api/etudes`), écrite par le lot P
// (`backend/app/etudes/abs/router.py`, branche feat/abs-plateforme-20261014).
//
// Les formes de réponse ci-dessous sont celles de ce routeur ; elles sont réunies ici, et nulle part
// ailleurs, pour qu'un écart avec le serveur se corrige dans ce seul fichier. Le serveur
// fait foi pour les droits (`droits`) : l'interface ne fait qu'en masquer ou griser les boutons, et
// ne s'en sert jamais pour ouvrir quoi que ce soit.
import axios from 'axios'
import { API } from '../../obs/types'
import { ROLE_CONSULTANT } from '../consultant'
import type { Document, StatutDocument } from './types'

export const STATUTS: Record<StatutDocument, string> = {
  generation: 'Génération en cours',
  echec: 'Échec de la génération',
  brouillon: 'Brouillon',
  en_relecture: 'En relecture',
  valide: 'Validée',
  livre: 'Livrée',
}

/** Les statuts qu'on choisit à la main ; `generation` et `echec` sont posés par la génération. */
export const STATUTS_MANUELS: StatutDocument[] = ['brouillon', 'en_relecture', 'valide', 'livre']

export interface Personne { id: string; nom: string | null; email?: string | null }

/** Le nom d'une personne servie par l'API (objet `{id, nom}`, ou texte). */
export function nomDe(p: Personne | string | null | undefined): string {
  if (!p) return ''
  return typeof p === 'string' ? p : p.nom || p.email || ''
}

export interface Droits {
  modifier: boolean
  rediger: boolean
  /** les statuts vers lesquels l'appelant peut faire passer le document */
  statuts: StatutDocument[]
  affecter: boolean
  historique: boolean
  pdf: boolean
}

/** Une ligne de `GET /api/etudes`. */
export interface EtudeResume {
  id: number | string
  type: 'abs'
  insee: string
  commune: string
  titre: string
  statut: StatutDocument
  version: number
  erreur?: string | null
  cree_le: string
  modifie_le?: string | null
  modifie_par?: Personne | string | null
  redaction_en_cours?: boolean
}

/** Un consultant affecté à une étude (`affectations`, servi à l'équipe seulement). */
export interface Affectation { user_id: string; nom: string | null; email: string | null; affecte_le?: string }

/** `GET /api/etudes/{id}` : le document, son statut, sa version et les droits de l'appelant. */
export interface Etude extends EtudeResume {
  /** null tant que la génération n'a rien produit (statut `generation` ou `echec`) */
  document: Document | null
  droits?: Droits
  /** l'interrupteur ETUDES_ABS_DIFFUSION_CLIENTS (vue client) : fermé par défaut */
  diffusion_clients?: boolean
  document_de_travail?: boolean
  affectations?: Affectation[]
}

export interface VersionResume {
  version: number
  statut: StatutDocument
  auteur_nom?: string | null
  le: string
  motif?: string | null
}

export interface VersionLue extends VersionResume { document: Document }

export interface Consultant extends Personne { organisation?: string | null; etudes?: number }

/** Ce que rend la création d'un compte consultant, une seule fois (mécanisme des comptes existant). */
export interface CompteCree {
  consultant?: Consultant
  identifiant?: string
  mot_de_passe?: string
  lien?: string
}

export const ROLES_EQUIPE = ['admin', 'direction']

/** Les droits d'un compte quand le serveur ne les a pas servis : ceux du tableau du CDC, § 7.2. */
export function droitsParDefaut(role: string | undefined): Droits {
  if (role && ROLES_EQUIPE.includes(role)) {
    return { modifier: true, rediger: true, statuts: [...STATUTS_MANUELS], affecter: true, historique: true, pdf: true }
  }
  if (role === ROLE_CONSULTANT) {
    return { modifier: true, rediger: true, statuts: ['brouillon', 'en_relecture'], affecter: false,
             historique: true, pdf: true }
  }
  return { modifier: false, rediger: false, statuts: [], affecter: false, historique: false, pdf: false }
}

const entetes = (token: string | null) => (token ? { headers: { Authorization: `Bearer ${token}` } } : {})

export function statutHttp(e: unknown): number | undefined {
  return (e as { response?: { status?: number } })?.response?.status
}

/** Un 409 de conflit de version (`{message, version}`) ; les autres 409 (document non modifiable,
    rédaction en cours) portent une phrase. */
export function conflitDeVersion(e: unknown): boolean {
  const detail = (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail
  return statutHttp(e) === 409 && (typeof detail !== 'string')
}

/** Les erreurs d'une sauvegarde refusée (422) : la liste du serveur, quelle que soit sa forme
    (`{message, erreurs: […]}`, liste de phrases, erreurs de validation pydantic, phrase seule). */
export function erreursServeur(e: unknown): string[] {
  const detail = (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail
  const lire = (x: unknown): string | null => {
    if (typeof x === 'string') return x
    if (x && typeof x === 'object' && 'msg' in x) {
      const o = x as { msg: unknown; loc?: unknown }
      const lieu = Array.isArray(o.loc) ? o.loc.filter(p => p !== 'body').join(' / ') : ''
      return lieu ? `${lieu} : ${String(o.msg)}` : String(o.msg)
    }
    return null
  }
  if (Array.isArray(detail)) return detail.map(lire).filter((x): x is string => !!x)
  if (detail && typeof detail === 'object' && Array.isArray((detail as { erreurs?: unknown }).erreurs)) {
    return ((detail as { erreurs: unknown[] }).erreurs).map(lire).filter((x): x is string => !!x)
  }
  if (detail && typeof detail === 'object' && typeof (detail as { message?: unknown }).message === 'string') {
    return [(detail as { message: string }).message]
  }
  const seul = lire(detail)
  return seul ? [seul] : []
}

export function messageErreur(e: unknown, quoi = 'La demande'): string {
  const s = statutHttp(e)
  if (s === 401) return 'Votre session a expiré : reconnectez-vous.'
  if (s === 403 || s === 404) return 'Cette étude n’existe pas ou ne vous est pas confiée.'
  const erreurs = erreursServeur(e)
  if (erreurs.length) return erreurs.join(' ')
  return `${quoi} n’a pas abouti. Réessayez dans un instant.`
}

const liste = <T,>(data: unknown, cle: string): T[] =>
  (Array.isArray(data) ? data : ((data as Record<string, unknown>)?.[cle] as T[] | undefined) ?? []) as T[]

export async function listerEtudes(token: string | null): Promise<EtudeResume[]> {
  const r = await axios.get(`${API}/etudes`, entetes(token))
  return liste<EtudeResume>(r.data, 'etudes')
}

export async function lireEtude(id: string | number, token: string | null): Promise<Etude> {
  return (await axios.get<Etude>(`${API}/etudes/${encodeURIComponent(String(id))}`, entetes(token))).data
}

export async function enregistrer(id: string | number, version: number, document: Document,
                                  token: string | null): Promise<Etude> {
  return (await axios.put<Etude>(`${API}/etudes/${encodeURIComponent(String(id))}`, { version, document },
    entetes(token))).data
}

export async function changerStatut(id: string | number, statut: StatutDocument, version: number,
                                    token: string | null): Promise<void> {
  await axios.post(`${API}/etudes/${encodeURIComponent(String(id))}/statut`, { statut, version }, entetes(token))
}

export async function rediger(id: string | number, token: string | null): Promise<void> {
  await axios.post(`${API}/etudes/${encodeURIComponent(String(id))}/rediger`, {}, entetes(token))
}

/** Une version telle que le routeur la sert : `cree_le` et `auteur: {id, nom}`. */
interface VersionServie {
  version: number; statut: StatutDocument; cree_le?: string; le?: string; motif?: string | null
  auteur?: Personne | null; auteur_nom?: string | null; document?: Document; contenu?: Document
}
const versionLisible = (v: VersionServie): VersionResume => ({
  version: v.version, statut: v.statut, le: v.cree_le ?? v.le ?? '', motif: v.motif ?? null,
  auteur_nom: v.auteur_nom ?? (v.auteur ? nomDe(v.auteur) : null),
})

export async function listerVersions(id: string | number, token: string | null): Promise<VersionResume[]> {
  const r = await axios.get(`${API}/etudes/${encodeURIComponent(String(id))}/versions`, entetes(token))
  return liste<VersionServie>(r.data, 'versions').map(versionLisible)
}

export async function lireVersion(id: string | number, n: number, token: string | null): Promise<VersionLue> {
  const r = await axios.get(`${API}/etudes/${encodeURIComponent(String(id))}/versions/${n}`, entetes(token))
  const d = r.data as VersionServie
  return { ...versionLisible(d), document: (d.document ?? d.contenu)! }
}

export async function creerAbs(insee: string, token: string | null): Promise<EtudeResume> {
  return (await axios.post<EtudeResume>(`${API}/etudes/abs`, { insee }, entetes(token))).data
}

export async function affecter(id: string | number, userIds: string[], token: string | null): Promise<void> {
  await axios.put(`${API}/etudes/${encodeURIComponent(String(id))}/affectations`, { user_ids: userIds }, entetes(token))
}

export async function listerConsultants(token: string | null): Promise<Consultant[]> {
  const r = await axios.get(`${API}/admin/consultants`, entetes(token))
  return liste<Consultant>(r.data, 'consultants')
}

export async function creerConsultant(email: string, nom: string, token: string | null): Promise<CompteCree> {
  return (await axios.post<CompteCree>(`${API}/admin/consultants`, { email, nom }, entetes(token))).data
}

/** « TerObs-ABS-<Commune>-<AAAA-MM-JJ>.pdf » (CDC § 7.5), sauf si le serveur nomme le fichier. */
export function nomPdf(commune: string, disposition?: string | null, le = new Date()): string {
  const m = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition ?? '')
  if (m) return decodeURIComponent(m[1])
  const propre = commune.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^A-Za-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
  return `TerObs-ABS-${propre}-${le.toISOString().slice(0, 10)}.pdf`
}

export async function telechargerPdf(id: string | number, commune: string, token: string | null): Promise<void> {
  const r = await axios.get(`${API}/etudes/${encodeURIComponent(String(id))}/pdf`, { responseType: 'blob', ...entetes(token) })
  telechargerFichier(r.data as Blob, nomPdf(commune, (r.headers as Record<string, string> | undefined)?.['content-disposition']))
}
